import { onPageIn, onMount, onDestroy } from '@/modules/_'

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const swiperWrapper = element.querySelector('.swiper-wrapper') as HTMLElement

  if (!swiperWrapper) return

  let isDown = false
  let startX = 0
  let scrollLeft = 0
  let currentTranslate = 0
  let animationId: number | null = null
  let lastTime = performance.now()
  let isPaused = false

  // Clone slides for seamless loop
  const slides = Array.from(swiperWrapper.children)
  slides.forEach((slide) => {
    const clone = slide.cloneNode(true) as HTMLElement
    swiperWrapper.appendChild(clone)
  })

  swiperWrapper.style.display = 'flex'
  swiperWrapper.style.willChange = 'transform'

  // Calculate total width after cloning
  let totalWidth = 0
  const speed = 50 // pixels per second

  const calculateWidth = () => {
    totalWidth = 0
    const allChildren = Array.from(swiperWrapper.children) as HTMLElement[]
    const halfCount = allChildren.length / 2

    // Measure only first half (original slides)
    for (let i = 0; i < halfCount; i++) {
      totalWidth += allChildren[i].offsetWidth
    }

    // Add gaps between original slides
    const gap = parseInt(getComputedStyle(swiperWrapper).gap) || 0
    totalWidth += gap * (halfCount - 1)

    console.log('Total width:', totalWidth)
    console.log('Half count:', halfCount)
    console.log('All children:', allChildren.length)
  }

  const animate = (currentTime: number) => {
    if (!isPaused && !isDown) {
      const deltaTime = (currentTime - lastTime) / 1000
      currentTranslate -= speed * deltaTime

      // Seamless loop
      if (currentTranslate <= -totalWidth) {
        currentTranslate += totalWidth
      }

      swiperWrapper.style.transform = `translateX(${currentTranslate}px)`
    }

    lastTime = currentTime
    animationId = requestAnimationFrame(animate)
  }

  const handleMouseEnter = () => {
    isPaused = true
  }

  const handleMouseLeave = () => {
    if (!isDown) {
      isPaused = false
      lastTime = performance.now()
    }
  }

  const handleMouseDown = (e: MouseEvent) => {
    isDown = true
    isPaused = true
    element.style.cursor = 'grabbing'
    startX = e.pageX - element.offsetLeft
    scrollLeft = currentTranslate
  }

  const handleMouseUp = () => {
    if (!isDown) return
    isDown = false
    element.style.cursor = 'grab'
    isPaused = false
    lastTime = performance.now()
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDown) return
    e.preventDefault()

    const x = e.pageX - element.offsetLeft
    const walk = (x - startX) * 1.5
    currentTranslate = scrollLeft + walk

    // Seamless loop during drag - FIX: update scrollLeft by same amount
    if (currentTranslate <= -totalWidth) {
      currentTranslate += totalWidth
      scrollLeft += totalWidth // Keep scrollLeft in valid range
    } else if (currentTranslate > 0) {
      currentTranslate -= totalWidth
      scrollLeft -= totalWidth // Keep scrollLeft in valid range
    }

    swiperWrapper.style.transform = `translateX(${currentTranslate}px)`
  }

  // Touch event handlers for mobile
  const handleTouchStart = (e: TouchEvent) => {
    isDown = true
    isPaused = true
    startX = e.touches[0].pageX - element.offsetLeft
    scrollLeft = currentTranslate
  }

  const handleTouchEnd = () => {
    if (!isDown) return
    isDown = false
    isPaused = false
    lastTime = performance.now()
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDown) return
    e.preventDefault()

    const x = e.touches[0].pageX - element.offsetLeft
    const walk = (x - startX) * 1.5
    currentTranslate = scrollLeft + walk

    // Seamless loop during drag - FIX: update scrollLeft by same amount
    if (currentTranslate <= -totalWidth) {
      currentTranslate += totalWidth
      scrollLeft += totalWidth // Keep scrollLeft in valid range
    } else if (currentTranslate > 0) {
      currentTranslate -= totalWidth
      scrollLeft -= totalWidth // Keep scrollLeft in valid range
    }

    swiperWrapper.style.transform = `translateX(${currentTranslate}px)`
  }

  onMount(() => {
    element.style.cursor = 'grab'
    element.style.userSelect = 'none'
    element.style.touchAction = 'pan-y' // Allow vertical scroll, prevent horizontal

    // Calculate width after mount
    setTimeout(() => {
      calculateWidth()
      animationId = requestAnimationFrame(animate)
    }, 100)

    // Desktop events
    element.addEventListener('mouseenter', handleMouseEnter)
    element.addEventListener('mouseleave', handleMouseLeave)
    element.addEventListener('mousedown', handleMouseDown)
    element.addEventListener('mouseup', handleMouseUp)
    element.addEventListener('mousemove', handleMouseMove)

    // Mobile events
    element.addEventListener('touchstart', handleTouchStart, { passive: false })
    element.addEventListener('touchend', handleTouchEnd)
    element.addEventListener('touchmove', handleTouchMove, { passive: false })
  })

  onDestroy(() => {
    if (animationId !== null) {
      cancelAnimationFrame(animationId)
    }

    element.removeEventListener('mouseenter', handleMouseEnter)
    element.removeEventListener('mouseleave', handleMouseLeave)
    element.removeEventListener('mousedown', handleMouseDown)
    element.removeEventListener('mouseup', handleMouseUp)
    element.removeEventListener('mousemove', handleMouseMove)
    element.removeEventListener('touchstart', handleTouchStart)
    element.removeEventListener('touchend', handleTouchEnd)
    element.removeEventListener('touchmove', handleTouchMove)
  })
}
